<template>
    <Teleport to="body">
        <Transition name="dialog-fade">
            <div v-if="modelValue" class="stf-dialog-overlay" @click="handleOverlayClick">
                <div class="stf-dialog-container" @click.stop>
                    <StfCard class="stf-dialog-card">
                        <template #title v-if="$slots.title">
                            <div class="stf-dialog-header">
                                <slot name="title"></slot>
                                <button
                                    v-if="closable"
                                    class="stf-dialog-close"
                                    aria-label="Close dialog"
                                    @click="handleClose"
                                >
                                    <StfIcon icon="xMark" size="small" color="black" />
                                </button>
                            </div>
                        </template>
                        <template #content>
                            <div class="stf-dialog-content" :style="{ minHeight: minHeight }">
                                <slot></slot>
                            </div>
                            <div v-if="$slots.actions" class="stf-dialog-actions">
                                <slot name="actions"></slot>
                            </div>
                        </template>
                    </StfCard>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { watch, onUnmounted } from 'vue';
import StfCard from '@/ui/card/StfCard.vue';
import StfIcon from '@/ui/icon/StfIcon.vue';

interface Props {
    modelValue: boolean;
    closable?: boolean;
    closeOnOverlayClick?: boolean;
    minHeight?: string;
    preventScroll?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    closable: true,
    closeOnOverlayClick: true,
    minHeight: 'auto',
    preventScroll: true
});

const emit = defineEmits<{
    'update:modelValue': [value: boolean]
}>();

const handleClose = () => {
    emit('update:modelValue', false);
};

const handleOverlayClick = () => {
    if (props.closeOnOverlayClick) {
        handleClose();
    }
};

// Prevent body scrolling when dialog is open
const toggleBodyScroll = (prevent: boolean) => {
    if (prevent) {
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = 'var(--scrollbar-width, 0px)';
    } else {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    }
};

// Watch for dialog state changes
watch(() => props.modelValue, (isOpen) => {
    if (props.preventScroll) {
        toggleBodyScroll(isOpen);
    }
}, { immediate: true });

// Clean up on component unmount
onUnmounted(() => {
    if (props.preventScroll) {
        toggleBodyScroll(false);
    }
});
</script>

<style lang="scss">
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

.stf-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 100px;
  z-index: 1000;
}

.stf-dialog-container {
  width: 100%;
  max-width: 735px;
  margin: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;

  @include mixins.mobile {
    margin: 16px;
    max-height: calc(100vh - 32px);
  }
}

.stf-dialog-card {
  margin: 0;
}

.stf-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.stf-dialog-content {
  display: flex;
  flex-direction: column;
}

.stf-dialog-actions {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid vars.$color-gray-200;
}

.stf-dialog-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: vars.$color-gray-100;
  }
}

// Transition animations
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.3s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}
</style> 
